import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { postChat, fetchSession, getInFlightRequest } from '../utils/chatRequestManager';

const API = '/api/ai';
const STORAGE_KEY = 'ai_chat_state';

const ChatContext = createContext(null);

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat должен использоваться внутри ChatProvider');
  return ctx;
};

const mapServerMessages = (serverMsgs) =>
  (serverMsgs || [])
    .filter(m => m.role !== 'system')
    .map(m => ({ role: m.role, content: m.content }));

const ChatProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', phone: '', email: '' });
  const [contactSent, setContactSent] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const navigatedRef = useRef(false);
  const loadingRef = useRef(false);
  const pollRef = useRef(null);
  const hydratedRef = useRef(false);

  const syncFromServer = useCallback(async (sid) => {
    if (!sid) return null;
    try {
      const data = await fetchSession(sid);
      const mapped = mapServerMessages(data.messages);
      if (mapped.length > 0) setMessages(mapped);
      if (data.contactInfo?.name || data.contactInfo?.phone || data.contactInfo?.email) {
        setContactForm(data.contactInfo);
      }
      if (data.status === 'completed') setContactSent(true);
      return data;
    } catch {
      return null;
    }
  }, []);

  const applyChatResponse = useCallback(async (res) => {
    const data = res.data;
    setSessionId(data.sessionId);
    await syncFromServer(data.sessionId);
    if (data.suggestedService) {
      setTimeout(() => {
        setMessages(prev => {
          const link = `🔗 Подробнее об услуге: [${data.suggestedService}]`;
          if (prev.some(m => m.content === link)) return prev;
          return [...prev, { role: 'assistant', content: link }];
        });
      }, 800);
    }
    if (data.askContacts) {
      setTimeout(() => setShowContacts(true), 1000);
    }
  }, [syncFromServer]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback((sid) => {
    stopPolling();
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      const data = await syncFromServer(sid);
      if (!data?.isPending) {
        setLoading(false);
        loadingRef.current = false;
        stopPolling();
      }
      if (attempts >= 45) {
        setLoading(false);
        loadingRef.current = false;
        stopPolling();
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '⚠️ Ответ задерживается. Попробуйте отправить сообщение ещё раз.'
        }]);
      }
    }, 2000);
  }, [syncFromServer, stopPolling]);

  // Восстановление после refresh / mount
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    let saved = null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) saved = JSON.parse(raw);
    } catch { /* ignore */ }

    if (saved) {
      setMessages(saved.messages || []);
      setSessionId(saved.sessionId || null);
      setContactSent(saved.contactSent || false);
      setShowContacts(saved.showContacts || false);
      setContactForm(saved.contactForm || { name: '', phone: '', email: '' });
      if (saved.isOpen) setIsOpen(true);
    }

    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});

    const inflight = getInFlightRequest();
    if (inflight?.promise) {
      setLoading(true);
      loadingRef.current = true;
      inflight.promise
        .then(applyChatResponse)
        .catch(e => {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: '⚠️ ' + (e.response?.data?.message || 'Ошибка соединения')
          }]);
        })
        .finally(() => {
          setLoading(false);
          loadingRef.current = false;
        });
      return;
    }

    if (saved?.loading && saved?.sessionId) {
      setLoading(true);
      loadingRef.current = true;
      syncFromServer(saved.sessionId).then(data => {
        if (data?.isPending) {
          startPolling(saved.sessionId);
        } else {
          setLoading(false);
          loadingRef.current = false;
        }
      });
    } else if (saved?.sessionId && (!saved.messages || saved.messages.length === 0)) {
      syncFromServer(saved.sessionId);
    }

    return () => stopPolling();
  }, [applyChatResponse, syncFromServer, startPolling, stopPolling]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        messages, sessionId, contactSent, showContacts, contactForm, isOpen, loading
      }));
    } catch { /* ignore */ }
  }, [messages, sessionId, contactSent, showContacts, contactForm, isOpen, loading]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const sendMessage = useCallback(async (text, files = null) => {
    const fileList = files || pendingFiles;
    if ((!text || !text.trim()) && !fileList.length) return;
    if (loadingRef.current) return;
    const userMsg = (text || '').trim();
    const attachNote = fileList.length
      ? `${userMsg ? userMsg + '\n' : ''}${fileList.map(f => `📎 ${f.name}`).join('\n')}`
      : userMsg;
    setMessages(prev => [...prev, { role: 'user', content: attachNote }]);
    setPendingFiles([]);
    setLoading(true);
    loadingRef.current = true;
    try {
      const res = await postChat(sessionId, userMsg, fileList);
      await applyChatResponse(res);
    } catch (e) {
      const data = e.response?.data;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data?.message || '⚠️ Не удалось получить ответ. Попробуйте ещё раз.'
      }]);
      if (data?.askContacts) setShowContacts(true);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [sessionId, applyChatResponse, pendingFiles]);

  const submitContacts = useCallback(async (form) => {
    if (!form.name && !form.phone && !form.email) return;
    if (!sessionId) return;
    setLoading(true);
    loadingRef.current = true;
    try {
      await axios.post(`${API}/feedback`, { sessionId, ...form });
      setContactSent(true);
      setShowContacts(false);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '⚠️ ' + (e.response?.data?.message || 'Ошибка')
      }]);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [sessionId]);

  const clearChat = useCallback(() => {
    stopPolling();
    setMessages([]);
    setSessionId(null);
    setShowContacts(false);
    setContactSent(false);
    setContactForm({ name: '', phone: '', email: '' });
    setPendingFiles([]);
    setLoading(false);
    navigatedRef.current = false;
    loadingRef.current = false;
    localStorage.removeItem(STORAGE_KEY);
  }, [stopPolling]);

  const openChat = useCallback(() => {
    setIsOpen(true);
    navigatedRef.current = false;
    if (messages.length === 0 && settings?.welcomeMessage) {
      setMessages([{ role: 'assistant', content: settings.welcomeMessage }]);
    }
  }, [messages.length, settings?.welcomeMessage]);

  return (
    <ChatContext.Provider value={{
      settings, isOpen, setIsOpen, messages, setMessages,
      sessionId, loading, showContacts, setShowContacts,
      contactForm, setContactForm, contactSent,
      navigatedRef, sendMessage, submitContacts, clearChat, openChat,
      pendingFiles, setPendingFiles
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export { ChatProvider };
